import "./App.css";

import { useState, useEffect, useRef, useCallback, memo } from "react";

import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import store from "store2";

import { storyFilterBoxState } from "./JotaiAtoms";

import {
  captionsState,
  projectPickerState,
  timeFilterState,
  negateLabelsState,
  searchState,
  newStoriesState,
  mergedStoriesState,
  joinStoriesState,
  reloadState,
  showSelectedStoriesState,
  peopleDataState,
  AccountInfo,
  displayedStoryCountState,
} from "./JotaiAtoms";
import { useAtomValue, useAtom, useSetAtom } from "jotai";

import { ButtonLink } from "./Image";

import { isPinned } from "./storydat";
import log from "./logger";
import * as u from "./utility";
import * as riki from "jsriki";

import { ShowStories } from "./ShowStories";
import { LabelBar } from "./LabelBar";

import { useHttpPost } from "./hooks";

import { normalizeCompositeLabel } from "./StoryLabelFilterBox";

import { MeddledStory, StoryItem, DummyStory } from "./StoryItem";
import * as lodash from "lodash";

const MemoShowStories = memo(ShowStories);

export type DataSource =
  | "deleted"
  | "popular"
  | "recent"
  | "labeled"
  | "stories"
  | "search"
  | "labeled_with_user"
  | "labeled_with_user_usage"
  | "events";

export const getCheckedThreads = (selected: {
  [key: string]: StoryItem | null;
}): string[] => {
  return Object.keys(selected);
};

type StoryDeletedToastProps = {
  item: StoryItem;
  onRestore: (story: StoryItem) => void;
};

function StoryDeletedToast({ item, onRestore }: StoryDeletedToastProps) {
  let story = u.getStoryKey(item) || "STORY";
  if (item.st_cap) {
    story = `[${item.st_cap}]`;
  }

  const handleClick = () => {
    if (onRestore) {
      onRestore(item);
    }
  };

  return (
    <Stack
      direction={"row"}
      alignItems={"center"}
      justifyContent={"space-between"}
      width="100%"
    >
      <Typography sx={{ fontSize: "13px", color: "var(--text-primary)" }}>
        {story} has been deleted
      </Typography>
      <Button
        onClick={handleClick}
        size="small"
        sx={{
          textTransform: "none",
          fontSize: "12px",
          fontWeight: 500,
          px: 1.5,
          py: 0.5,
          minWidth: "auto",
          color: "var(--accent-primary)",
          "&:hover": {
            backgroundColor: "var(--surface-hover)",
          },
        }}
      >
        Undo
      </Button>
    </Stack>
  );
}
export function usePrevious<Type>(value: Type | undefined) {
  const ref = useRef<Type>();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

export const makeStoriesRequest = (
  people: Map<string, AccountInfo[]>,
  userLabels?: string[],
  labels?: string[]
) => {
  let accounts: any = undefined;
  if (userLabels) {
    accounts = {};
    for (const u of userLabels) {
      const name = u.slice(1); // remove leading @
      for (const accs of people.values()) {
        for (const acc of accs) {
          if (
            acc.name === name ||
            acc.email === name ||
            acc.username === name ||
            `${acc.app}:${acc.id}` == name
          ) {
            accounts[u] = accs;
            break;
          }
        }
      }
    }
  }

  if (accounts && lodash.isEmpty(accounts)) {
    accounts = undefined;
  }

  if (accounts || labels) {
    return { labeled_with_user_usage: true, accounts, labels };
  }

  return undefined;
};

const categorizeLabels = (labels?: string[]) => {
  let labelFilters: string[] | undefined = undefined;
  let userFilters: string[] | undefined = undefined;
  if (labels && labels.length > 0) {
    for (const f of labels) {
      if (f.startsWith("@")) {
        if (!userFilters) {
          userFilters = [f];
        } else {
          userFilters.push(f);
        }
      } else {
        if (!labelFilters) {
          labelFilters = [f];
        } else {
          labelFilters.push(f);
        }
      }
    }
  }

  return [labelFilters, userFilters];
};

type FetchStoriesProps = {
  collapseAll: number;
  filterId?: string;
  timestampRef?: string;
  deleted?: boolean;
  popular?: boolean;
  recent?: boolean;
  events?: boolean;
  onLabelCreated?: (label: string) => void;
  onFetch?: (scrollUp: boolean) => void;
};

export function FetchStories({
  collapseAll,
  filterId,
  timestampRef,
  deleted,
  popular,
  recent,
  events,
  onLabelCreated,
  onFetch,
}: FetchStoriesProps) {
  const [data, setData] = useState<StoryItem[]>();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState(null);
  const [filtersInUse, setFiltersInUse] = useState<string[]>();
  const selectedFilters = useAtomValue(storyFilterBoxState);
  const [captions, setCaptions] = useAtom(captionsState);
  const timeFilter = useAtomValue(timeFilterState);
  const negateLabels = useAtomValue(negateLabelsState);
  const [newStories, setNewStories] = useAtom(newStoriesState);
  const [mergedStories, setMergedStories] = useAtom(mergedStoriesState);
  const [search, setSearch] = useAtom(searchState);
  const [lastKeyOlder, setLastKeyOlder] = useState<{
    PK: string;
    SK: string;
    PK2: string;
    SK2: string;
  }>();
  const [lastKeyNewer, setLastKeyNewer] = useState<{
    PK: string;
    SK: string;
    PK2: string;
    SK2: string;
  }>();
  const [now, setNow] = useState(u.getCurrentTimestamp());
  const [numPeriods, setNumPeriods] = useState(0);
  const [dataSource, setDataSource] = useState<DataSource>();
  const [showSelectedStories, setShowSelectedStories] = useAtom(
    showSelectedStoriesState
  );
  const selectedStories = useAtomValue(joinStoriesState);
  const reloadData = useAtomValue(reloadState);
  const project = useAtomValue(projectPickerState);
  const previousNegate = usePrevious(negateLabels);
  const httpPost = useHttpPost();
  const people = useAtomValue(peopleDataState);
  const setDisplayedStoryCount = useSetAtom(displayedStoryCountState);
  const [showPagination, setShowPagination] = useState(false);
  const bottomSentinelRef = useRef<HTMLDivElement>(null);

  // console.log(`fetch stories. prev data ${data ? data.length : null}`);

  // useEffect(() => {
  //   console.log("data");
  // }, [data]);
  // useEffect(() => {
  //   console.log("isLoading");
  // }, [isLoading]);
  // useEffect(() => {
  //   console.log("error");
  // }, [error]);
  // useEffect(() => {
  //   console.log("filtersInUse");
  // }, [filtersInUse]);
  // useEffect(() => {
  //   console.log("captions");
  // }, [captions]);
  // useEffect(() => {
  //   console.log("newStories");
  // }, [newStories]);
  // useEffect(() => {
  //   console.log("mergedStories");
  // }, [mergedStories]);
  // useEffect(() => {
  //   console.log("search");
  // }, [search]);
  // useEffect(() => {
  //   console.log("lastKeyOrder");
  // }, [lastKeyOlder]);
  // useEffect(() => {
  //   console.log("lastKeyNewer");
  // }, [lastKeyNewer]);
  // useEffect(() => {
  //   console.log("numPeriods");
  // }, [numPeriods]);
  // useEffect(() => {
  //   console.log("dataSource");
  // }, [dataSource]);
  // useEffect(() => {
  //   console.log("showSelectedStories");
  // }, [showSelectedStories]);

  useEffect(() => {
    setDisplayedStoryCount(data?.length || 0);
  }, [data, setDisplayedStoryCount]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setShowPagination(entry.isIntersecting);
        });
      },
      { threshold: 0.1 }
    );

    if (bottomSentinelRef.current) {
      observer.observe(bottomSentinelRef.current);
    }

    return () => {
      if (bottomSentinelRef.current) {
        observer.unobserve(bottomSentinelRef.current);
      }
    };
  }, [data]);

  const getProjectFilters = (): string[] => {
    return selectedFilters && selectedFilters[project]
      ? selectedFilters[project]
      : [];
  };

  const processReceivedData = (
    request: any,
    res: StoryItem[],
    source?: DataSource
  ) => {
    // console.log(`received stoiries ${res.length}`)

    // console.log("processReceivedData start");
    if (request.direction === "forward") {
      res.sort((a, b) => Number(b.SK2) - Number(a.SK2));
    }

    // console.log(`setting data ${res.length}`);

    let changed = false;
    if ((data && data.length > 0 && !res) || (res && res.length > 0 && !data)) {
      changed = true;
    } else if (data && data.length !== res.length) {
      changed = true;
    } else if (
      data &&
      data.length > 0 &&
      (data[0].SK2 !== res[0].SK2 ||
        data[data.length - 1].SK2 !== res[res.length - 1].SK2)
    ) {
      changed = true;
    }

    const decompressText = async (arr: StoryItem[]) => {
      const copy: StoryItem[] = [];
      for (const item of arr) {
        const pp = (item as any).st_pp;
        if (pp && typeof pp === "string") {
          copy.push({
            ...item,
            st_pp: await u.parseJsonFromBinary(pp),
          } as StoryItem);
        } else {
          copy.push(item);
        }
      }
      // console.log(`setData ${res.length}`);
      setData(copy);
    };

    decompressText(res);

    // console.log(`setDataSource ${source}`);
    setDataSource(source);

    const proj = project;
    if (res && proj) {
      // see if there are changes in captions
      let captionsChanged = true;
      if (captions && proj in captions) {
        captionsChanged = false;
        for (let d of res) {
          const cap = d.st_cap;
          if (cap) {
            if (d.SK !== captions[proj][cap]) {
              captionsChanged = true;
              break;
            }
          }
        }
      }

      if (captionsChanged) {
        // there are changes in captions and we want to update the states
        let caps = captions ? { ...captions } : {};
        if (!(proj in caps)) {
          caps[proj] = {};
        } else {
          caps[proj] = { ...captions[proj] };
        }

        for (let d of res) {
          if (d.st_cap) {
            caps[proj][d.st_cap] = d.SK;
          }
        }
        store("Captions", JSON.stringify(caps));
        setCaptions(caps);
      }
    }

    if (res && onFetch) {
      const scrollUp: boolean = request.direction !== "more" && changed;
      // console.log(scrollUp);
      onFetch(scrollUp);
    }

    // console.log("processReceivedData done");
  };

  const fetchDataPyApi = async (
    input_request: any,
    requestFilters?: string[]
  ) => {
    // console.log("fetchDataPyApi");

    const request = { ...input_request };
    if (input_request.direction) {
      request.direction =
        input_request.direction === "forward" ? "forward" : "backward";
    }

    httpPost("stories", request)
      .then((response) => {
        let res = response.data;
        let source: DataSource | undefined = undefined;
        if (request.deleted) {
          res = res.deleted;
          source = "deleted";
        } else if (request.popular) {
          res = res.popular;
          source = "popular";
        } else if (request.recent) {
          res = res.recent;
          source = "recent";
        } else if (request.labeled) {
          res = res.labeled;
          source = "labeled";
        } else if (request.stories) {
          res = res.stories;
          source = "stories";
        } else if (request.search) {
          res = res.search;
          source = "search";
        } else if (request.labeled_with_user) {
          res = res.labeled_with_user;
          source = "labeled_with_user";
        } else if (request.labeled_with_user_usage) {
          res = res.labeled_with_user_usage;
          source = "labeled_with_user_usage";
        } else if (request.events) {
          res = res.events;
          source = "events";
        } else {
          res = null;
        }

        // console.log(`fetched from ${source} : ${res ? res.length : -1}`);

        if (
          requestFilters &&
          u.equalArrays(getProjectFilters(), requestFilters)
        ) {
          // console.log("processing");
          processReceivedData(request, res, source);
        } else {
          log.warn(
            `skip reload because of mismatching filters ${getProjectFilters()} - ${requestFilters}`
          );
        }

        setFiltersInUse(requestFilters);
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        log.debug(err);

        if (!data) {
          setError(err.message);
          setData(undefined);
        }
        setLoading(false);
      });
  };

  const fetchData = async (request: any, requestFilters?: string[]) => {
    // console.log("fetchData");
    if (
      (true &&
        request.stories &&
        !request.story &&
        request.direction !== "forward" &&
        !request.start &&
        !request.base_time) ||
      (request.labeled &&
        !request.negate &&
        request.label.length === 1 &&
        !u.isLabelExpression(request.label[0]))
    ) {
      httpPost("query", request)
        .then((response) => {
          if (request.stories) {
            // console.log(`queried storires ${response.data.stories.length}`);
            processReceivedData(request, response.data.stories, "stories");
          } else if (request.labeled) {
            // console.log(`queried labeled ${response.data.labeled.length}`);
            processReceivedData(request, response.data.labeled, "labeled");
          }

          setFiltersInUse(requestFilters);
          setError(null);
          setLoading(false);
        })
        .catch((e) => {
          // fallback to the old and slow api
          fetchDataPyApi(request, requestFilters);
        });
    } else {
      fetchDataPyApi(request, requestFilters);
    }
  };

  const getData = async (
    project: string,
    requestFilters?: string[],
    key?: unknown,
    direction?: unknown,
    baseTime?: string,
    timePeriod?: unknown
  ) => {
    let request: any = { project: project };

    if (key) {
      request.start = key;
    }

    // this is a hacky way of doing things, valid values for dir: [forward, backward]
    // this is what the underlying api expects, but i also inject the third value [forward, backward, more]
    // which has bo be mapped back to [forward, backward] when doing the api call
    request.direction = direction || "backward";
    // request.limit = 3;

    if (baseTime) {
      request.base_time = baseTime.padEnd(17, "0");
    }

    let [labelFilters, userFilters] = categorizeLabels(requestFilters);

    if (filterId && filterId.startsWith("@")) {
      const r = makeStoriesRequest(people, [filterId], labelFilters);
      if (r) {
        request = { ...request, ...r };
      } else {
        return;
      }
    } else if (filterId) {
      request = { ...request, stories: true, story: filterId };
      if (timestampRef) {
        request.timeref = timestampRef;
      }
    } else if (deleted) {
      request = { ...request, deleted: true };
    } else if (popular) {
      const sub = makeStoriesRequest(people, userFilters, labelFilters);
      request = {
        ...request,
        popular: true,
        labels: sub?.labels,
        accounts: sub?.accounts,
      };
      delete request.direction;
    } else if (recent) {
      const sub = makeStoriesRequest(people, userFilters, labelFilters);
      request = {
        ...request,
        popular: true,
        labels: sub?.labels,
        accounts: sub?.accounts,
      };
      delete request.direction;
    } else if (events) {
      request = {
        ...request,
        events: true,
        label: labelFilters,
        local_time: new Date().toString(),
      };
    } else if (labelFilters || userFilters) {
      const r = makeStoriesRequest(people, userFilters, labelFilters);
      if (r) {
        request = { ...request, ...r };
      } else {
        return;
      }
    } else {
      request = { ...request, stories: true };
    }

    if (timePeriod) {
      request.period = timePeriod;
    } else {
      request.period = timeFilter;
    }
    request.negate = negateLabels;

    if (events) {
      // not supported attributes
      delete request.period;
      delete request.negate;
      delete request.label;
    }

    fetchData(request, requestFilters);
  };

  const initiateDataUpdate = (
    project: string,
    key?: { PK: string; SK: string; PK2: string; SK2: string },
    direction?: string,
    baseTime?: string,
    timePeriod?: string
  ) => {
    if (project) {
      setLoading(true);
      const filters = getProjectFilters();
      getData(
        project,
        filters ? [...filters] : undefined,
        key,
        direction,
        baseTime,
        timePeriod
      );
    }
  };

  const updateContent = (project: string) => {
    setNumPeriods(0);
    setNow(u.getCurrentTimestamp());
    initiateDataUpdate(project);
  };

  useEffect(() => {
    if (search) {
      return;
    }

    // optimization: do not reload content if no filters selected and negate flag changes
    if (
      previousNegate !== undefined &&
      previousNegate !== negateLabels &&
      getProjectFilters().length === 0
    ) {
      return;
    }

    if (project && selectedFilters && !events && !deleted) {
      updateContent(project);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters, reloadData, timeFilter, project, negateLabels]);

  useEffect(() => {
    if (search) {
      return;
    }

    if (project && (events || deleted)) {
      updateContent(project);
    }
  }, [project]);

  useEffect(() => {
    // we handle only regular search here, if this is a user search
    // it will go throught StoryLabelFilterBox
    if (search && typeof search === "string") {
      setSearch("");
      let request: any;
      const filters = getProjectFilters();
      if (typeof search === "string") {
        request = { search: true, query: search };
      }
      // else {
      //   request = {
      //     labeled_with_user: true,
      //     accounts: search,
      //     period: timeFilter,
      //   };
      // }
      fetchData(request, filters ? [...filters] : undefined);
    }
  }, [search]);

  useEffect(() => {
    if (!mergedStories || mergedStories.length < 2) {
      return;
    }

    const mainStory = mergedStories[0];
    const merged = new Set();
    const mergedCount = mergedStories.length - 1;
    for (let s of mergedStories.slice(1)) {
      merged.add(s);
    }

    setData((prev) => {
      if (!prev) {
        return prev;
      } else {
        let next = [];
        for (const s of prev) {
          if (!merged.has(s.SK)) {
            if (s.SK === mainStory) {
              next.push({ ...s, st_len: (s.st_len || 0) + mergedCount });
            } else {
              next.push(s);
            }
          }
        }
        return next;
      }
    });

    setMergedStories([]);
  }, [mergedStories]);

  useEffect(() => {
    if (!newStories || !newStories.length) {
      return;
    }
    const matchFilters = (story: StoryItem | DummyStory) => {
      const filters = getProjectFilters();
      if (!filters || !filters.length) {
        return true;
      }

      const expressionFilters = filters.filter((e) => u.isLabelExpression(e));
      let plainLabelFilters = filters.filter((e) => !u.isLabelExpression(e));

      const storyLabels = story.st_labels || [];
      for (let l of storyLabels) {
        if (plainLabelFilters.includes(l)) {
          return true;
        }
      }
      for (let e of expressionFilters) {
        if (riki.evaluate_expression(e, storyLabels.join(" "))) {
          return true;
        }
      }
      return false;
    };

    let filtered = newStories.filter(matchFilters);
    let dummyUpdate = newStories.filter(
      (e) => (e as MeddledStory).internal_update && !matchFilters(e)
    );

    if (filtered.length || dummyUpdate.length) {
      setData((prev) => {
        if (!prev) {
          return filtered;
        } else {
          let newData = [];
          let uniq = new Set();
          for (let s of filtered.concat(dummyUpdate)) {
            uniq.add(s.SK);
            const dummy = (s as MeddledStory).old_dummy_message;
            if (dummy) {
              uniq.add(dummy.SK);
            }

            if (!(s as MeddledStory).internal_update) {
              newData.push(s);
            } else {
              const search = (s as MeddledStory).old_dummy_message.SK;

              // include the updated item only if the original is still there
              const found = prev.find(
                (e) =>
                  e.SK === search ||
                  ((e as MeddledStory).old_dummy_message &&
                    (e as MeddledStory).old_dummy_message.SK === search)
              );

              if (found) {
                newData.push(s);
              }
            }
          }

          // remove duplicates which may be created as a result of story merging
          for (let s of prev) {
            if (!uniq.has(s.SK)) {
              uniq.add(s.SK);

              // pinned stories stay where they are -at the top of the list
              // or they don't? that's debatable
              if (false && isPinned(s)) {
                newData = [s, ...newData];
              } else {
                newData.push(s);
              }
            }
          }

          return newData;
        }
      });
    }

    setNewStories([]);
  }, [newStories]);

  useEffect(() => {
    if (showSelectedStories) {
      const selected = getCheckedThreads(selectedStories);
      setShowSelectedStories(false);

      if (selected.length > 0) {
        const request = { stories: true, story: selected };
        httpPost("stories", request)
          .then((response) => {
            let res = response.data.stories;
            let source: DataSource = "stories";
            processReceivedData(request, res, source);
            setError(null);
            setLoading(false);
          })
          .catch((err) => {});
      }
    }
  }, [showSelectedStories]);

  useEffect(() => {
    // console.log(`data has updated indeed: ${data ? data.length : "null"}`);
    if (filterId && project && data && data.length > 0) {
      let name = data[0].st_cap;
      if (!name && data[0].SK4) {
        name = Number(data[0].SK4).toString();
      }

      if (name) {
        document.title = `topcat | ${name}`;
      }
    }
  }, [data]);

  const handleDelete = useCallback(
    (item: StoryItem) => {
      const deleteTopic = async (item: StoryItem) => {
        try {
          httpPost("label", { delete: { stories: [item["SK"]] } });
        } catch (err) {
        } finally {
        }
      };

      const handleUndo = (item: StoryItem) => {
        const request = async (item: StoryItem) => {
          try {
            httpPost("label", { restore: { stories: [item["SK"]] } });
          } catch (err) {
          } finally {
          }
        };

        request(item);
        setData((prev: StoryItem[] | undefined) => {
          if (!prev) {
            return [item];
          }

          const dupe = prev.findIndex((e) => e.SK === item.SK);
          if (dupe === -1) {
            let found = prev.findIndex((e) => e.SK2 < item.SK2);
            if (found === -1) {
              found = prev.length;
            }
            const newData = [...prev];
            newData.splice(found, 0, item);
            return newData;
          }
          return prev;
        });
      };

      deleteTopic(item);
      setData((prev) => {
        const found: number = prev
          ? prev.findIndex((e) => e.SK === item.SK)
          : -1;
        if (found !== -1) {
          const newData = [...prev!];
          newData.splice(found, 1);
          setData(newData);

          toast.warning(
            <StoryDeletedToast
              item={item}
              onRestore={(item) => handleUndo(item)}
            />
          );
          return newData;
        }
        return prev;
      });
    },
    [setData, httpPost]
  );

  const pinItem = (item: StoryItem, pin: boolean) => {
    if (pin) {
      setData((prev) => {
        if (!prev) {
          return prev;
        }

        const index = prev.findIndex((e) => e.SK === item.SK);
        if (index !== -1) {
          const result = Array.from(prev);

          let [removed] = result.splice(index, 1);
          let edited = { ...removed };
          edited.st_updated_ts = edited.SK2;
          edited.SK2 = "pin:0";

          result.splice(0, 0, edited);
          return result;
        }
        return prev;
      });
    } else {
      setData((prev) => {
        if (!prev) {
          return prev;
        }

        const index = prev.findIndex((e) => e.SK === item.SK);
        if (index !== -1) {
          const result = Array.from(prev);
          let [removed] = result.splice(index, 1);
          let edited = { ...removed };

          const ts = edited.st_updated_ts || edited.SK3;
          edited.SK2 = ts;

          let i = 0;
          while (i < result.length && isPinned(result[i])) {
            i = i + 1;
          }

          while (i < result.length && result[i].SK2 > removed.SK2) {
            i = i + 1;
          }

          result.splice(i, 0, edited);
          return result;
        }
        return prev;
      });
    }
  };

  const handlePin = useCallback(
    (item: StoryItem) => {
      const request = async (pin: boolean) => {
        try {
          httpPost("label", { pin: { topic: item.SK, pin: pin } });
        } catch (err) {
        } finally {
        }
      };

      const pin = !isPinned(item);
      request(pin);
      pinItem(item, pin);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setData, httpPost]
  );

  const handleRestore = useCallback(
    (item: StoryItem) => {
      const request = async (item: StoryItem) => {
        try {
          httpPost("label", { restore: { stories: [item["SK"]] } });
        } catch (err) {
        } finally {
        }
      };
      setData((prev) => {
        if (!prev) {
          return prev;
        }
        return prev.filter((x) => x["SK"] !== item["SK"]);
      });

      request(item);
    },
    [setData, httpPost]
  );

  const handleUpdatedStoryLabels = useCallback(
    (storyId: string, newLabels: string[]) => {
      // console.log(`handleUpdateStoryLabels ${JSON.stringify(newLabels)}`);
      // console.log(`filtersInUse ${JSON.stringify(filtersInUse)}`);

      if (
        dataSource === "search" ||
        getMode() === "onestory" ||
        getMode() === "events"
      ) {
        return;
      }

      if ((!filtersInUse || 0 === filtersInUse.length) && !negateLabels) {
        return;
      }

      // [1604]: ignore people filters
      if (filtersInUse?.filter((e) => !e.startsWith("@")).length === 0) {
        return;
      }

      function removeFromList<Type extends { SK: string }>(
        prev: Type[]
      ): Type[] {
        const found = prev.findIndex((x) => x.SK === storyId);
        if (found >= 0) {
          const result = Array.from(prev);
          result.splice(found, 1);
          return result;
        }
        return prev;
      }

      let plainLabels = filtersInUse
        ? filtersInUse.filter((e) => !u.isLabelExpression(e))
        : [];
      const newLabelsNorm = newLabels.map((e) => normalizeCompositeLabel(e));
      const intersection = plainLabels.filter(
        (n) => newLabelsNorm.indexOf(n) > -1
      );

      let matchFound = intersection.length > 0;
      const expressions = filtersInUse
        ? filtersInUse.filter((e) => u.isLabelExpression(e))
        : [];
      for (let expr of expressions) {
        if (riki.evaluate_expression(expr, newLabels.join(" "))) {
          matchFound = true;
          break;
        }
      }

      if ((matchFound && negateLabels) || (!matchFound && !negateLabels)) {
        setData((prev) => (prev ? removeFromList(prev) : prev));
        setNewStories(removeFromList);
        return;
      }
    },
    [setData, setNewStories, filtersInUse, negateLabels]
  );

  const handleNewLabel = useCallback(
    (label: string) => {
      if (onLabelCreated) {
        onLabelCreated(label);
      }
    },
    [onLabelCreated]
  );

  const getMode = ():
    | "onestory"
    | "popular"
    | "recent"
    | "deleted"
    | "events"
    | "regular" => {
    if (filterId && data && data.length === 1) {
      return "onestory";
    } else if (popular) {
      return "popular";
    } else if (recent) {
      return "recent";
    } else if (deleted) {
      return "deleted";
    } else if (events) {
      return "events";
    }

    return "regular";
  };

  const handlePageOlder = () => {
    const proj = project;
    if (events) {
      initiateDataUpdate(proj);
    } else {
      const days = u.timePeriodToDays(timeFilter);

      if (days === -1) {
        let key = null;

        if (data && data.length > 0) {
          const last = data[data.length - 1];
          const pk2 = last.PK.slice(0, -2) + "#o";
          key = { PK: last.PK, SK: last.SK, SK2: last.SK2, PK2: pk2 };
          initiateDataUpdate(proj, key);
          setLastKeyOlder(key);
          setLastKeyNewer(undefined);
        } else if (lastKeyNewer) {
          initiateDataUpdate(proj);
        }
      } else {
        const baseTime = (
          parseFloat(now) -
          (1 + numPeriods) * days * 24 * 60 * 60
        ).toString();
        setNumPeriods((prev) => prev + 1);
        initiateDataUpdate(proj, undefined, undefined, baseTime);
      }
    }
  };

  const handlePageNewer = () => {
    const proj = project;
    if (events) {
      initiateDataUpdate(proj, undefined, "forward");
    } else {
      const days = u.timePeriodToDays(timeFilter);

      if (days === -1) {
        let key = null;

        if (data && data.length > 0) {
          const last = data[0];
          const pk2 = last.PK.slice(0, -2) + "#o";
          key = { PK: last.PK, SK: last.SK, SK2: last.SK2, PK2: pk2 };
          initiateDataUpdate(proj, key, "forward");
          setLastKeyNewer(key);
          setLastKeyOlder(undefined);
        } else if (lastKeyOlder) {
          initiateDataUpdate(proj, undefined, "forward");
        }
      } else {
        if (numPeriods > 0) {
          const baseTime = (
            parseFloat(now) -
            (numPeriods - 1) * days * 24 * 60 * 60
          ).toString();
          setNumPeriods((prev) => prev - 1);
          initiateDataUpdate(proj, undefined, undefined, baseTime);
        }
      }
    }
  };

  const handleMore = () => {
    const days = u.timePeriodToDays(timeFilter);

    if (days !== -1) {
      setNumPeriods((prev) => prev + 1);
      initiateDataUpdate(
        project,
        undefined,
        "more",
        undefined,
        (days * (numPeriods + 2)).toString() + "d"
      );
    }
  };

  const showPaginationButtons = () => {
    let [labelFilters, userFilters] = categorizeLabels(getProjectFilters());
    const numLabelsSelected = labelFilters?.length;

    return (
      events ||
      lastKeyNewer ||
      lastKeyOlder ||
      (data && data.length > 0 && !numLabelsSelected)
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100%" }}>
      <Box sx={{ flex: 1 }}>
        {isLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 8,
            }}
          >
            <CircularProgress
              size={32}
              sx={{
                color: "var(--accent-primary)",
              }}
            />
          </Box>
        )}
        {error && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                color: "var(--text-secondary)",
              }}
            >
              Failed to fetch data
            </Typography>
          </Box>
        )}
        {!isLoading && !error && data && data.length === 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 12,
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: 500,
                color: "var(--text-primary)",
                mb: 0.5,
              }}
            >
              No stories found
            </Typography>
            <Typography
              sx={{
                fontSize: "13px",
                color: "var(--text-secondary)",
              }}
            >
              Try adjusting your filters or search
            </Typography>
          </Box>
        )}
        {!isLoading && !error && data && data.length > 0 && (
          <>
            <LabelBar stories={data} />
            <MemoShowStories
              data={data}
              onPin={handlePin}
              onDelete={handleDelete}
              onRestore={handleRestore}
              onUpdateLabels={handleUpdatedStoryLabels}
              collapseAll={collapseAll}
              mode={getMode()}
              onLabelCreated={handleNewLabel}
              dataSource={dataSource}
            />
          </>
        )}
      </Box>

      {/* Pagination buttons at bottom of content */}
      {showPaginationButtons() && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 3,
            py: 4,
            mt: 1,
          }}
        >
          <Button
            onClick={handlePageNewer}
            disabled={
              !data || (data.length === 0 && lastKeyNewer) ? true : false
            }
            sx={{
              textTransform: "none",
              fontSize: "11px",
              fontWeight: 400,
              px: 0,
              py: 0,
              minWidth: "auto",
              color: "var(--text-secondary)",
              backgroundColor: "transparent",
              border: 0,
              textDecoration: "underline",
              "&:hover": {
                backgroundColor: "transparent",
                color: "var(--text-primary)",
                textDecoration: "underline",
              },
              "&:disabled": {
                color: "var(--text-tertiary)",
                textDecoration: "none",
              },
            }}
          >
            {events ? "← future" : "← newer"}
          </Button>
          <Button
            onClick={handlePageOlder}
            disabled={
              !data || (data.length === 0 && lastKeyOlder) ? true : false
            }
            sx={{
              textTransform: "none",
              fontSize: "11px",
              fontWeight: 400,
              px: 0,
              py: 0,
              minWidth: "auto",
              color: "var(--text-secondary)",
              backgroundColor: "transparent",
              border: 0,
              textDecoration: "underline",
              "&:hover": {
                backgroundColor: "transparent",
                color: "var(--text-primary)",
                textDecoration: "underline",
              },
              "&:disabled": {
                color: "var(--text-tertiary)",
                textDecoration: "none",
              },
            }}
          >
            {events ? "past →" : "older →"}
          </Button>
        </Box>
      )}
      {!showPaginationButtons() && timeFilter && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 4,
            mt: 1,
          }}
        >
          <Button
            onClick={handleMore}
            sx={{
              textTransform: "none",
              fontSize: "11px",
              fontWeight: 400,
              px: 0,
              py: 0,
              minWidth: "auto",
              color: "var(--text-secondary)",
              backgroundColor: "transparent",
              border: 0,
              textDecoration: "underline",
              "&:hover": {
                backgroundColor: "transparent",
                color: "var(--text-primary)",
                textDecoration: "underline",
              },
            }}
          >
            show more
          </Button>
        </Box>
      )}
    </Box>
  );
}
